import React, { useMemo, useState } from 'react';
import { useLocation } from 'wouter';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { cn } from '@/lib/utils';
import {
  ACCOUNT_NAV_LABELS,
  ACCOUNT_PAGE_IDS,
  ACCOUNT_PATHS,
  type AccountPageId,
} from '@/lib/accountPaths';
import { useAccountRecord } from '@/pages/account/AccountRecordContext';
import {
  searchAccountSettings,
  visibleAccountPages,
} from '@/pages/account/accountSearchIndex';
import { AccountIdentityAvatar } from '@/pages/account/AccountIdentity';

interface AccountSidebarProps {
  /** Render for the mobile nav drawer — always expanded, taller rows for touch. */
  inDrawer?: boolean;
  /** Called after a nav row navigates, so the drawer can close itself. */
  onNavigated?: () => void;
}

/**
 * AR-380 — the Account center's Level 2 column.
 *
 * Head: the identity block (photo, name, brand) and the settings search.
 * Body: every page the member can see — v15 made this a FLAT list of nine, with
 * no group headings and no overview row above it (D-ACCT-25, D-ACCT-28).
 *
 * Terms and privacy are not here any more: they moved under the omnibar
 * (D-CHROME-LEGAL-01).
 *
 * The search finds a field by its label and opens the page holding it — a
 * result names the page and the card, so the reader knows where they landed.
 */
export const AccountSidebar: React.FC<AccountSidebarProps> = ({
  inDrawer = false,
  onNavigated,
}) => {
  const [location, navigate] = useLocation();
  const { record, role, setFocusCard } = useAccountRecord();
  const [collapsed, setCollapsed] = useState(false);
  const [query, setQuery] = useState('');

  const visible = useMemo(() => new Set(visibleAccountPages(role)), [role]);
  const hits = useMemo(() => searchAccountSettings(query, role), [query, role]);

  const isActive = (page: AccountPageId) => location === ACCOUNT_PATHS[page];

  const go = (path: string, card?: string) => {
    navigate(path);
    setQuery('');
    // A result opens the card holding the field: the page scrolls to it and
    // marks it for a moment.
    setFocusCard(card ?? null);
    onNavigated?.();
  };

  /* No explicit height: the flex body sizes the column, so a banner above it
     cannot push the last rows under the fold. The nav below already scrolls. */

  if (collapsed && !inDrawer) {
    return (
      <div
        data-app-chrome
        className="flex w-8 flex-col items-center border-r border-gray-200 bg-gray-50/50 pt-4 dark:border-gray-700 dark:bg-gray-900/50"
      >
        <button
          onClick={() => setCollapsed(false)}
          className="rounded p-1 text-gray-400 transition-colors hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
          title="Expand Account sidebar"
          aria-label="Expand Account sidebar"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div
      data-app-chrome
      data-account-sidebar
      className={cn(
        'flex flex-col bg-gray-50/50 dark:bg-gray-900/50',
        inDrawer ? 'w-full' : 'min-h-0 w-60 border-r border-gray-200 dark:border-gray-700',
      )}
    >
      <div className={cn('px-3 pb-2', inDrawer ? 'pt-3' : 'pt-4')}>
        {!inDrawer && (
          <button
            onClick={() => setCollapsed(true)}
            className="mb-1 rounded p-1 text-gray-400 transition-colors hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
            title="Collapse sidebar"
            aria-label="Collapse sidebar"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
        )}

        {/* The identity block — who and which business this center edits. */}
        <div className="mb-1.5 flex items-center gap-2.5 border-b border-gray-200 px-1.5 pb-2.5 dark:border-gray-700">
          <AccountIdentityAvatar size="sm" />
          <span className="min-w-0">
            <b className="block truncate text-[14.5px] leading-tight text-gray-900 dark:text-gray-100">
              {record.you.first} {record.you.last}
            </b>
            <small className="block truncate text-[12.5px] text-gray-500 dark:text-gray-400">
              {record.biz.brand}
            </small>
          </span>
        </div>

        {/* The settings search. */}
        <div className="px-0.5 pb-2">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search settings"
            aria-label="Search settings"
            autoComplete="off"
            className="w-full rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-[13.5px] text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
          />
          {query.trim() ? (
            <div className="mt-1.5 flex flex-col gap-0.5" data-account-search-results>
              {hits.length ? (
                hits.map((h) => (
                  <button
                    key={`${h.label}-${h.page}-${h.card}`}
                    type="button"
                    onClick={() => go(h.path, h.cardKey ?? h.card)}
                    className="w-full rounded-md px-2 py-1 text-left hover:bg-gray-100 dark:hover:bg-gray-800"
                  >
                    <b className="block text-[13.5px] font-semibold leading-tight text-gray-900 dark:text-gray-100">
                      {h.label}
                    </b>
                    <small className="block text-[12px] text-gray-500 dark:text-gray-400">
                      {h.card ? `${h.pageLabel}, ${h.card}` : h.pageLabel}
                    </small>
                  </button>
                ))
              ) : (
                <p className="px-2 py-1 text-[12.5px] text-gray-500 dark:text-gray-400">
                  No setting matches that.
                </p>
              )}
            </div>
          ) : null}
        </div>
      </div>

      <nav
        className={cn('space-y-0.5 px-3 pb-2', !inDrawer && 'flex-1 overflow-y-auto')}
        aria-label="Account"
      >
        {ACCOUNT_PAGE_IDS.filter((p) => visible.has(p)).map((page) => {
          const active = isActive(page);
          return (
            <button
              key={page}
              type="button"
              onClick={() => go(ACCOUNT_PATHS[page])}
              aria-current={active ? 'page' : undefined}
              className={cn(
                'flex w-full items-center rounded-lg px-3 text-left text-sm transition-colors',
                inDrawer ? 'min-h-11 py-2.5' : 'py-2',
                active
                  ? 'bg-orange-100 font-medium text-orange-700 dark:bg-orange-900/50 dark:text-orange-300'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200',
              )}
            >
              {ACCOUNT_NAV_LABELS[page]}
            </button>
          );
        })}
      </nav>
    </div>
  );
};

export default AccountSidebar;
